import { useState, useEffect } from "react"
import { PaymentElement, useStripe, useElements, Elements } from "@stripe/react-stripe-js"
import {
	Button,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components"
import { api } from "../system/api.js"
import { getStripePromise } from "../system/stripePromise.js"
import { buildStripeAppearance } from "../system/stripeAppearance.js"
import Loader from "./Loader.jsx"

function PaymentMethodForm({ onClose, onSuccess }) {
	const stripe = useStripe()
	const elements = useElements()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)

	const handleSubmit = async (e) => {
		e.preventDefault()

		if (!stripe || !elements) {
			return
		}

		setLoading(true)
		setError(null)

		const { error: submitError, setupIntent } = await stripe.confirmSetup({
			elements,
			confirmParams: {
				return_url: window.location.origin + "/billing",
			},
			redirect: "if_required",
		})

		if (submitError) {
			setError(submitError.message)
			setLoading(false)
			return
		}

		try {
			await api("payment_method_attach", {
				payment_method_id: setupIntent.payment_method,
			})
			onSuccess()
		} catch {
			setError("Failed to save payment method.")
		} finally {
			setLoading(false)
		}
	}

	const paymentElementOptions = {
		layout: "tabs",
	}

	return (
		<form onSubmit={handleSubmit}>
			<div className="mb-4">
				<PaymentElement id="payment-element" options={paymentElementOptions} />
			</div>

			{error && <p className="text-caption text-danger mb-3">{error}</p>}

			<DialogFooter className="gap-2 mt-2">
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="text-label"
					onClick={onClose}
					disabled={loading}
				>
					Cancel
				</Button>
				<Button
					type="submit"
					size="sm"
					className="text-label"
					disabled={!stripe || loading}
				>
					{loading ? "Adding..." : "Add card"}
				</Button>
			</DialogFooter>
		</form>
	)
}

export default function PaymentMethodModal({ onClose, onSuccess }) {
	const [stripePromise, setStripePromise] = useState(null)
	const [setupData, setSetupData] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	useEffect(() => {
		let cancelled = false
		api("payment_method_setup")
			.then((data) => {
				if (cancelled) return
				setSetupData(data)
				setStripePromise(getStripePromise(data.publicKey))
			})
			.catch(() => {
				if (cancelled) return
				setError("Failed to initialize payment setup.")
			})
			.finally(() => {
				if (!cancelled) setLoading(false)
			})
		return () => {
			cancelled = true
		}
	}, [])

	return (
		<>
			<DialogHeader>
				<DialogTitle className="text-section text-foreground">
					Add payment method
				</DialogTitle>
				<DialogDescription className="text-caption text-muted-foreground">
					Save a card for future subscription charges.
				</DialogDescription>
			</DialogHeader>
			<div>
				{loading ? (
					<Loader />
				) : error ? (
					<p className="text-caption text-danger">{error}</p>
				) : (
					<Elements
						stripe={stripePromise}
						options={{
							clientSecret: setupData?.client_secret,
							appearance: buildStripeAppearance(),
							loader: "auto",
						}}
					>
						<PaymentMethodForm onClose={onClose} onSuccess={onSuccess} />
					</Elements>
				)}
			</div>
		</>
	)
}
